import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import PropTypes from 'prop-types'

// Components
import { Button } from '../../components/Button'
import { DataTable } from '../../components/DataTable'
import { SidePanel } from '../../components/SidePanel'
import { TextEditor, TextAreaEditor } from '../../components/CustomEditor'
import { TextInput } from '../../components/TextInput'

// Services
import {
  createClientBenchmark,
  updateBenchmarkDisplay,
  updateClientBenchmark,
} from '../../services/benchmarks.service'

// Utils
import { toast } from '../../utils/helpers'

const BenchmarkPanel = ({ benchmark = null, clientId, closePanel, onChanged, panelOpen }) => {
  const isEditing = !!benchmark?.id

  const [loading, setLoading] = useState(false)

  const handleSuccess = (m) => toast(m, 'success')
  const handleErrors = (m) => toast(m, 'error')

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm({
    defaultValues: { name: '', component: '' },
  })

  useEffect(() => {
    if (panelOpen) {
      reset({
        name: benchmark?.name || '',
        component: benchmark?.component || '',
      })
    }
  }, [panelOpen, benchmark?.id, benchmark?.name, benchmark?.component])

  const onSave = async (data) => {
    if (isEditing) {
      await updateClientBenchmark(
        clientId,
        { id: benchmark.id, ...data },
        handleErrors,
        setLoading,
        (m) => {
          handleSuccess(m)
          closePanel()
        },
      )
    } else {
      await createClientBenchmark(clientId, data, handleErrors, setLoading, (m) => {
        handleSuccess(m)
        closePanel()
      })
    }
  }

  const onRowEditComplete = async ({ newData }) => {
    await updateBenchmarkDisplay(
      clientId,
      benchmark.id,
      newData.id,
      { reportName: newData.reportName, description: newData.description },
      handleErrors,
      () => {},
      (m) => {
        handleSuccess(m)
        onChanged()
      },
    )
  }

  return (
    <SidePanel
      open={panelOpen}
      setOpen={closePanel}
      title={isEditing ? 'Edit Benchmark' : 'Add Benchmark'}
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextInput
            disabled={loading}
            fullWidth
            label="Benchmark Name"
            name="name"
            error={errors.name && 'This field is required'}
            {...register('name', { required: true })}
          />

          <TextInput
            disabled={loading}
            fullWidth
            label="Component Name"
            name="component"
            error={errors.component && 'This field is required'}
            {...register('component', { required: true })}
          />
        </div>

        <div className="flex flex-row justify-end gap-4">
          <Button background="bg-gray" label="Cancel" loading={loading} onClick={closePanel} />
          <Button
            label={isEditing ? 'Update' : 'Save'}
            loading={loading}
            onClick={handleSubmit(onSave)}
          />
        </div>

        {isEditing && (
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Report Names & Descriptions</h3>

            <DataTable
              data={benchmark.displays || []}
              columns={[
                {
                  field: 'reportName',
                  header: 'Report Name',
                  editor: TextEditor,
                  style: { minWidth: '200px' },
                },
                {
                  field: 'description',
                  header: 'Description',
                  editor: TextAreaEditor,
                  style: { minWidth: '300px' },
                },
              ]}
              emptyMessage="No report names configured for this benchmark."
              hoverEffect={false}
              onRowEditComplete={onRowEditComplete}
              areRowsEditable
            />
          </div>
        )}
      </div>
    </SidePanel>
  )
}

BenchmarkPanel.propTypes = {
  benchmark: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    component: PropTypes.string,
    displays: PropTypes.array,
  }),
  clientId: PropTypes.string.isRequired,
  closePanel: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
  panelOpen: PropTypes.bool.isRequired,
}

export default BenchmarkPanel
